import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserProfile, updateUserProfile, generatedStaticUrl } from "../api/apis";
import { Youtube, Instagram, Copy, Edit, Save, X, User, Mail, Phone, Calendar, MapPin, Twitch, Twitter, ExternalLink, Link as LinkIcon, Camera, Package } from "lucide-react";
import { getFromLocalStorage, removeFromLocalStorage } from "../utils/localStorage";
import ProtectedRoute from "../Components/ProtectedRoute";


const Profile = () => {
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState({
    username: "",
    emailId: "",
    createdAt: new Date(),
    user_type: "viewer",
    membershipPlan: "free",
    youtubeLink: "",
    instagramLink: "",
    description: "",
    profilePhoto: ""
  });
  const [editedProfile, setEditedProfile] = useState({ ...userProfile });
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generatedUrl, setGeneratedUrl] = useState(null);
  const [userRole, setUserRole] = useState(getFromLocalStorage("userRole"));
  const [profilePhoto, setProfilePhoto] = useState("");
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const fetchedRef = useRef(false);

  const token = getFromLocalStorage("token");
  const user = getFromLocalStorage("user");
  const isLoggedIn = (token && token.trim() !== "");

  const checkTimeout = useCallback(() => {
    const expirationTime = getFromLocalStorage("expirationTime");
    if (!expirationTime || Date.now() > parseInt(expirationTime, 10)) {
      removeFromLocalStorage("token");
      removeFromLocalStorage("adminToken");
      removeFromLocalStorage("user");
      removeFromLocalStorage("expirationTime");
      return false;
    }
    return true;
  }, []);

  const handleLogout = useCallback(() => {
    removeFromLocalStorage("token");
    removeFromLocalStorage("adminToken");
    removeFromLocalStorage("user");
    navigate("/");
    window.location.reload();
  }, [navigate]);

  const handleGenerateStaticUrl = async () => {
    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    try {
      const response = await generatedStaticUrl({
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        },
      });

      if (response.data.status === "success") {
        // Always use the current frontend origin so links point to the app (e.g., http://localhost:3000)
        const frontendBase = (typeof window !== 'undefined' && window.location?.origin) ? window.location.origin : '';
        setGeneratedUrl(`${frontendBase}${response.data.url}`);
        toast.success("URL Generated Successfully!");
      } else {
        toast.error(response.data.error || "Failed to generate URL");
      }
    } catch (error) {
      console.error("Error generating URL:", error);
      if (error.response?.status === 401) {
        handleLogout();
      } else {
        toast.error("Error Generating URL");
      }
    }
  };

  const fetchUserProfile = useCallback(async () => {
    if (fetchedRef.current) return;

    if (!isLoggedIn) {
      navigate("/login");
      return;
    }

    if (!checkTimeout()) {
      handleLogout();
      toast.error("Session expired. Please log in again.");
      return;
    }

    try {
      setLoading(true);
      const response = await getUserProfile({
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        },
      });

      if (response.data.status === "success" && response.data.user) {
        // Some APIs return { user: { ... } }, others { user: { user: { ... } } }
        const userObject = response.data.user.user || response.data.user;

        if (userObject) {
          const profileData = {
            username: userObject.username || '',
            emailId: userObject.emailId || '',
            createdAt: userObject.createdAt ? new Date(userObject.createdAt) : new Date(),
            user_type: userObject.user_type || 'viewer',
            membershipPlan: userObject.membershipPlan || 'free',
            youtubeLink: userObject.youtubeLink || '',
            instagramLink: userObject.instagramLink || '',
            description: userObject.description || '',
            profilePhoto: userObject.profilePhoto || ''
          };
          setUserProfile(profileData);
          setEditedProfile(profileData);

          // Derive and sync role with localStorage using database truth
          const mapRoleFromDatabase = (u) => {
            const utype = u.user_type || 'viewer';
            const mplan = u.membershipPlan || 'free';
            if (utype === 'creator' && mplan === 'pro') return 'creator_pro';
            if (utype === 'creator') return 'creator';
            return 'user';
          };
          const derivedRole = mapRoleFromDatabase(profileData);
          try {
            const existing = getFromLocalStorage('user') || {};
            const merged = { ...existing, ...profileData };
            localStorage.setItem('user', JSON.stringify(merged));
            localStorage.setItem('userRole', derivedRole);
          } catch { }
        } else {
          toast.error("Error loading user data");
        }
      } else if (response.data.status === "jwterror") {
        toast.error("Session expired. Please log in again.");
        handleLogout();
      } else {
        try {
          const localUser = getFromLocalStorage("user");
          if (localUser) {
            setUserProfile(localUser);
            setEditedProfile(localUser);
          }
        } catch (e) {
          console.error("Error parsing local user data", e);
        }
      }
      fetchedRef.current = true;
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        handleLogout();
      } else {
        try {
          const localUser = getFromLocalStorage("user");
          if (localUser) {
            setUserProfile(localUser);
            setEditedProfile(localUser);
          }
        } catch (e) {
          console.error("Error parsing local user data", e);
        }
        toast.error("Failed to load profile");
      }
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn, navigate, token, user, checkTimeout, handleLogout]);

  useEffect(() => {
    if (fetchedRef.current) return;

    const timeoutId = setTimeout(() => {
      fetchUserProfile();
    }, 100);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [fetchUserProfile]);


  const handleSaveProfile = async () => {
    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    try {
      setSaving(true);
      const updateData = {
        username: editedProfile.username,
        emailId: editedProfile.emailId,
        user_type: editedProfile.user_type,
        youtubeLink: editedProfile.youtubeLink || '',
        instagramLink: editedProfile.instagramLink || '',
        description: editedProfile.description || '',
        profilePhoto: editedProfile.profilePhoto || '',
        updatedAt: new Date().toISOString()
      };

      const response = await updateUserProfile(updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        },
      });

      if (response.data.updated === true) {
        toast.success("Profile updated successfully!");
        localStorage.setItem("userRole", editedProfile.user_type);
        setUserRole(editedProfile.user_type);
        setUserProfile(editedProfile);
        setIsEditing(false);
      } else {
        toast.error(response.data.error || "Failed to update profile.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        handleLogout();
      } else {
        toast.error(error.response?.data?.error || "An error occurred while updating profile.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleToggleLiveStatus = async () => {
    if (!checkTimeout()) {
      handleLogout();
      return;
    }

    try {
      const newLiveStatus = !userProfile.isLive;
      const updateData = {
        isLive: newLiveStatus,
        updatedAt: new Date().toISOString()
      };

      const response = await updateUserProfile(updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "User-Info": user?.emailId || user?.username || user
        },
      });

      if (response.data.updated === true) {
        setUserProfile(prev => ({ ...prev, isLive: newLiveStatus }));
        setEditedProfile(prev => ({ ...prev, isLive: newLiveStatus }));

        // Update localStorage
        const localUser = getFromLocalStorage('user') || {};
        localStorage.setItem('user', JSON.stringify({ ...localUser, isLive: newLiveStatus }));

        toast.success(newLiveStatus ? "You are now LIVE! 🔴" : "You went offline");
      } else {
        toast.error(response.data.error || "Failed to update live status.");
      }
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
        handleLogout();
      } else {
        toast.error(error.response?.data?.error || "An error occurred while updating live status.");
      }
    }
  };

  const generateStreamURL = () => {
    if (editedProfile.username) {
      return `${window.location.origin}/stream/${editedProfile.username}`;
    }
    return "";
  };

  const copyStreamURL = () => {
    const url = generateStreamURL();
    if (url) {
      navigator.clipboard.writeText(url).then(() => {
        toast.success("Stream URL copied to clipboard!");
      }).catch(() => {
        toast.error("Failed to copy URL");
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const handleCancelEdit = () => {
    setEditedProfile(userProfile);
    setIsEditing(false);
  };

  const handleInputChange = (field, value) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a valid image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size should be less than 5MB');
      return;
    }

    try {
      setPhotoUploading(true);

      // Convert to base64 for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64Image = e.target.result;
        setProfilePhoto(base64Image);
        handleInputChange('profilePhoto', base64Image);
        toast.success('Profile photo updated!');
        setPhotoUploading(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
      setPhotoUploading(false);
    }
  };

  const triggerPhotoUpload = () => {
    fileInputRef.current?.click();
  };

  const getProfilePhotoSrc = () => {
    const photo = editedProfile.profilePhoto || userProfile.profilePhoto || profilePhoto;
    if (photo) return photo;

    // Generate avatar with first letter of username
    const firstLetter = (editedProfile.username || userProfile.username || 'U').charAt(0).toUpperCase();
    return `data:image/svg+xml;base64,${btoa(`
      <svg width="100" height="100" xmlns="http://www.w3.org/2000/svg">
        <circle cx="50" cy="50" r="50" fill="linear-gradient(135deg, #fbbf24, #f59e0b)"/>
        <text x="50" y="65" font-family="Arial, sans-serif" font-size="40" font-weight="bold" text-anchor="middle" fill="#000">${firstLetter}</text>
      </svg>
    `)}`;
  };

  // Determine creator status from loaded profile OR localStorage fallback
  const localUser = getFromLocalStorage('user') || {};
  const storedUserRole = getFromLocalStorage('userRole');
  // Effective role using membership plan
  const effectiveRole = (() => {
    if (storedUserRole === 'creator_pro') return 'creator_pro';
    const utype = userProfile?.user_type || localUser?.user_type || 'viewer';
    const mplan = userProfile?.membershipPlan || localUser?.membershipPlan || 'free';
    if (utype === 'creator' && mplan === 'pro') return 'creator_pro';
    if (utype === 'creator') return 'creator';
    return 'user';
  })();
  const isCreator = effectiveRole === 'creator' || effectiveRole === 'creator_pro';

  return (
    <ProtectedRoute>
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 text-white">
        <main className="pt-6 pb-12 px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-7xl mx-auto">
            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center space-x-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-lg flex items-center justify-center">
                  <User className="w-6 h-6 text-black" />
                </div>
                <div>
                  <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-yellow-600 bg-clip-text text-transparent">
                    Profile
                  </h1>
                  <p className="text-gray-400">Manage your account and streaming preferences</p>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-yellow-400"></div>
              </div>
            ) : (
              <div className="bg-black/30 rounded-xl p-6 border border-gray-800 backdrop-blur-sm">
                {/* Profile Photo Section */}
                <div className="flex flex-col items-center mb-8">
                  <div className="relative group">
                    <div className="w-32 h-32 rounded-full overflow-hidden border-4 border-yellow-500/30 bg-gradient-to-br from-yellow-400 to-yellow-600">
                      <img
                        src={getProfilePhotoSrc()}
                        alt="Profile"
                        className="w-full h-full object-cover"
                      />
                    </div>
                    {isEditing && (
                      <button
                        onClick={triggerPhotoUpload}
                        disabled={photoUploading}
                        className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        {photoUploading ? (
                          <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white"></div>
                        ) : (
                          <Camera className="w-6 h-6 text-white" />
                        )}
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    className="hidden"
                  />
                  {isEditing && (
                    <p className="text-sm text-gray-400 mt-2 text-center">
                      Click on photo to change
                    </p>
                  )}
                </div>

                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-white">Profile Information</h2>
                  {!isEditing ? (
                    <button
                      onClick={() => setIsEditing(true)}
                      className="flex items-center space-x-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-4 py-2 rounded-lg font-semibold hover:from-yellow-500 hover:to-yellow-700 transition-colors"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                  ) : (
                    <div className="flex space-x-2">
                      <button
                        onClick={handleSaveProfile}
                        disabled={saving}
                        className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors disabled:opacity-50"
                      >
                        <Save className="w-4 h-4" />
                        {saving ? "Saving..." : "Save Changes"}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg font-semibold transition-colors"
                      >
                        <X className="w-4 h-4" />
                        <span>Cancel</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Username */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">Username</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={isEditing ? editedProfile.username : userProfile.username}
                        onChange={(e) => handleInputChange('username', e.target.value)}
                        disabled={!isEditing}
                        className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors disabled:opacity-60"
                        placeholder="Enter username"
                      />
                    </div>
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">Email</label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="email"
                        value={isEditing ? editedProfile.emailId : userProfile.emailId}
                        onChange={(e) => handleInputChange('emailId', e.target.value)}
                        disabled={true}
                        className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-gray-400 placeholder-gray-400 focus:outline-none cursor-not-allowed opacity-60"
                        placeholder="Enter email"
                        title="Email can only be changed by an administrator"
                      />
                    </div>
                  </div>

                  {/* Account Created */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">Account Created</label>
                    <div className="relative">
                      <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={userProfile.createdAt ? new Date(userProfile.createdAt).toLocaleDateString() : 'Not set'}
                        disabled
                        className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed"
                      />
                    </div>
                  </div>

                  {/* Role */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-300">Role</label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        value={(effectiveRole || 'user').replace('_', ' ')}
                        disabled
                        className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-gray-400 cursor-not-allowed"
                      />
                    </div>
                  </div>
                </div>

                {/* Creator-specific fields */}
                {(userProfile.user_type === "creator" || userRole === "creator") && (
                  <div className="mt-6 space-y-6">
                    {/* Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                      <textarea
                        value={isEditing ? editedProfile.description : userProfile.description}
                        onChange={(e) => handleInputChange('description', e.target.value)}
                        disabled={!isEditing}
                        rows={3}
                        className="w-full px-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors disabled:opacity-60 resize-none"
                        placeholder="Enter a short bio about yourself"
                      />
                    </div>

                    {/* Social Media Links */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">YouTube Link</label>
                        <div className="relative">
                          <Youtube className="absolute left-3 top-1/2 transform -translate-y-1/2 text-red-400 w-5 h-5" />
                          <input
                            type="url"
                            value={isEditing ? editedProfile.youtubeLink : userProfile.youtubeLink}
                            onChange={(e) => handleInputChange('youtubeLink', e.target.value)}
                            disabled={!isEditing}
                            className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors disabled:opacity-60"
                            placeholder="Enter YouTube link"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-300">Instagram Link</label>
                        <div className="relative">
                          <Instagram className="absolute left-3 top-1/2 transform -translate-y-1/2 text-pink-400 w-5 h-5" />
                          <input
                            type="url"
                            value={isEditing ? editedProfile.instagramLink : userProfile.instagramLink}
                            onChange={(e) => handleInputChange('instagramLink', e.target.value)}
                            disabled={!isEditing}
                            className="w-full pl-10 pr-4 py-3 bg-black/30 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-yellow-500 transition-colors disabled:opacity-60"
                            placeholder="Enter Instagram link"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Creator Tools */}
                    <div className="pt-6 border-t border-gray-700">
                      <h3 className="text-lg font-semibold text-white mb-4">Creator Tools</h3>
                      <div className="space-y-4">


                        <button
                          onClick={handleGenerateStaticUrl}
                          className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors flex items-center space-x-2"
                        >
                          <LinkIcon className="w-5 h-5" />
                          <span>Generate Stream URL</span>
                        </button>

                        {generatedUrl && (
                          <div className="space-y-2">
                            <label className="block text-sm font-medium text-gray-300">Your Stream URL</label>
                            <div className="flex items-center space-x-2">
                              <input
                                type="text"
                                value={generatedUrl}
                                readOnly
                                className="flex-1 px-3 py-2 bg-black/30 border border-gray-700 rounded-lg text-white text-sm"
                              />
                              <a
                                href={generatedUrl || '#'}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg hover:from-blue-600 hover:to-blue-700 transition-colors"
                                title="Open in new tab"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                              <button
                                onClick={() => copyToClipboard(generatedUrl)}
                                className="p-2 bg-gradient-to-r from-yellow-400 to-yellow-600 text-black rounded-lg hover:from-yellow-500 hover:to-yellow-700 transition-colors"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        )}

                        {/* Card Inventory Button - Only for Creator Pro */}
                        {effectiveRole === 'creator_pro' && (
                          <Link
                            to="/card-inventory"
                            className="bg-gradient-to-r from-yellow-400 to-yellow-600 hover:from-yellow-500 hover:to-yellow-700 hover:text-black text-black px-4 py-2 rounded-md font-semibold transition-colors inline-flex items-center space-x-1.5 text-sm"
                          >
                            <Package className="w-4 h-4" />
                            <span>Card Inventory</span>
                          </Link>
                        )}

                        {/* Social Media Display */}
                        {(userProfile.youtubeLink || userProfile.instagramLink) && (
                          <div className="pt-4 border-t border-gray-700">
                            <h4 className="text-sm font-medium text-gray-300 mb-2">Social Media</h4>
                            <div className="flex items-center space-x-4">
                              {userProfile.youtubeLink && (
                                <a
                                  href={userProfile.youtubeLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-red-500 hover:text-red-400 transition-colors"
                                >
                                  <Youtube className="w-8 h-8" />
                                </a>
                              )}
                              {userProfile.instagramLink && (
                                <a
                                  href={userProfile.instagramLink}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-pink-500 hover:text-pink-400 transition-colors"
                                >
                                  <Instagram className="w-8 h-8" />
                                </a>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </ProtectedRoute>
  );
};

export default Profile;
